import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { api, unwrap } from "@/api/client";
import { keys, meQuery, systemInfoQuery } from "@/api/queries";
import { Button } from "@/design/button";
import { Field, Input } from "@/design/form";
import { useShake } from "@/design/motion";
import { AuthLayout } from "@/features/auth/AuthLayout";
import { errorMessage } from "@/lib/errors";

export const Route = createFileRoute("/login")({
  validateSearch: z.object({ redirect: z.string().optional() }),
  beforeLoad: async ({ context }) => {
    const info = await context.queryClient.ensureQueryData(systemInfoQuery);
    if (info.edition === "local") throw redirect({ to: "/" });
    if (info.setup_required) throw redirect({ to: "/setup" });
    if (await context.queryClient.fetchQuery(meQuery)) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

function safeRedirect(target: string | undefined): string {
  // Only same-origin paths; never an absolute URL from the query string.
  return target && target.startsWith("/") && !target.startsWith("//") ? target : "/";
}

function LoginPage() {
  const { t } = useTranslation();
  const { redirect: target } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const login = useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/auth/login", { body: { username, password } })),
    onSuccess: async (me) => {
      queryClient.setQueryData(keys.me, me);
      await navigate({ to: safeRedirect(target) });
    },
  });

  // A refused sign-in shakes the form, once per attempt.
  const shake = useShake<HTMLFormElement>(login.isError && login.submittedAt);

  function submit(e: FormEvent) {
    e.preventDefault();
    login.mutate();
  }

  return (
    <AuthLayout title={t("auth.signInTitle")}>
      <form ref={shake} className="w-full max-w-[360px]" onSubmit={submit}>
        <h1 className="m-0 mb-1.5 text-2xl font-[650]">{t("auth.signInTitle")}</h1>
        <p className="m-0 mb-6 text-ink-3">{t("auth.signInIntro")}</p>
        <div className="flex flex-col gap-3.5">
          <Field label={t("auth.username")}>
            {(p) => (
              <Input autoComplete="username" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} {...p} />
            )}
          </Field>
          <Field label={t("auth.password")} help={login.isError ? undefined : t("auth.forgot")}>
            {(p) => (
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                {...p}
              />
            )}
          </Field>
          {login.isError && (
            <p role="alert" className="m-0 text-[13px] text-danger">
              {errorMessage(t, login.error)}
            </p>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="mt-2 w-full"
            disabled={!username || !password || login.isPending}
            loading={login.isPending}
          >
            {t("auth.signIn")}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
